import { io, Socket } from 'socket.io-client';

class SocketSingleton {
  private static instance: SocketSingleton | null = null;
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private lastErrorLogTime = 0;
  private logInterval = 10000;
  private isBypassMode = false;

  private constructor() {}

  public static getInstance(): SocketSingleton {
    if (!SocketSingleton.instance) {
      SocketSingleton.instance = new SocketSingleton();
      SocketSingleton.instance.initSocket();
    }
    return SocketSingleton.instance;
  }

  private formatErrorMessage(error: any): string {
    if (error.description?.message) {
      return `Socket Error: ${error.description.message}`;
    }

    if (error.message) {
      return `Socket Error: ${error.message}`;
    }

    return 'Unknown socket error';
  }

  private logError(error: any): void {
    const now = Date.now();
    if (now - this.lastErrorLogTime > this.logInterval) {
      const errorMessage = this.formatErrorMessage(error);

      console.error(`❌ ${JSON.stringify(errorMessage)}`);

      if (process.env.NODE_ENV === 'development') {
        console.debug('Socket debug details:', {
          socketId: this.socket?.id,
          timestamp: new Date().toISOString(),
          reconnectAttempts: this.reconnectAttempts,
          isBypassMode: this.isBypassMode,
        });
      }

      this.lastErrorLogTime = now;
    }
  }

  private initSocket(): void {
    if (this.socket) return;
    const baseUrl = process.env.NEXT_PUBLIC_SOCKET_URL;

    if (!baseUrl) {
      console.warn('⚠️ Missing environment variable NEXT_PUBLIC_SOCKET_URL');
      this.isBypassMode = true;
      return;
    }

    this.socket = io(baseUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 2000,
      timeout: 30000,
      withCredentials: true,
    });

    this.socket.on('connect', () => {
      console.log(`✅ Socket connected with id: ${this.socket?.id}`);
      this.reconnectAttempts = 0;
      this.isBypassMode = false;
    });

    this.socket.on('disconnect', (reason) => {
      console.warn(`⚡ Socket disconnected: ${reason}`);
    });

    this.socket.on('connect_error', (error) => {
      this.logError(error);

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        this.reconnectSocket();
      } else {
        console.warn(
          '⚠️ Maximum reconnect attempts reached. Entering bypass mode.',
        );
        this.socket?.disconnect();
        this.socket = null;
        this.isBypassMode = true;
      }
    });
  }

  private reconnectSocket() {
    console.log(
      `🔄 Attempting to reconnect socket... (Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );
    this.socket?.disconnect();
    this.socket = null;
    setTimeout(() => this.initSocket(), 2000);
  }

  public getSocket(): Socket | null {
    if (this.isBypassMode) {
      console.warn('⚠️ Socket is in bypass mode. Skipping socket operations.');
      return null;
    }

    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket is not connected.');
      return null;
    }
    return this.socket;
  }

  public disconnect(): void {
    if (this.socket) {
      console.log('🚀 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public listenWithSocketId(
    eventName: string,
    callback: (...args: any[]) => void,
  ): void {
    if (this.isBypassMode) {
      console.warn(
        `⚠️ Skipping listenWithSocketId in bypass mode for event: ${eventName}`,
      );
      return;
    }

    const socket = this.getSocket();
    if (!socket) return;
    socket.on(socket.id + eventName, callback);
  }

  public listen(event: string, callback: (...args: any[]) => void): void {
    if (this.isBypassMode) {
      console.warn(`⚠️ Skipping listen in bypass mode for event: ${event}`);
      return;
    }

    const socket = this.getSocket();
    if (!socket) return;
    socket.on(event, callback);
  }

  public emit(event: string, ...args: any[]): void {
    if (this.isBypassMode) {
      console.warn(`⚠️ Skipping emit in bypass mode for event: ${event}`);
      return;
    }

    const socket = this.getSocket();
    if (!socket) return;
    socket.emit(event, ...args);
  }
}

const socketInstance = SocketSingleton.getInstance();
export default socketInstance;
